import React from 'react'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import uniqBy from 'lodash/uniqBy'

import { InlineInput, Tags } from '@ztechapps/webui.input'

import type { InlineInputContentOptions, InlineInputProps, InlineInputValidateFunction } from '@ztechapps/webui.input'
import type { FilterQuery } from '@ztechapps/common.contract'
import {
  InventoryBySerialNumberStatusEnum,
  SerialNumberTypeEnum,
  type InventoryBySerialNumber,
} from '@ztechapps/common.contract'
import { InventoryBySerialNumberSelect } from './InventoryBySerialNumberSelect'
import { Badge } from 'react-bootstrap'

/**
 * Inline serial number selector .
 * Allows selecting serial numbers for components that require serial number tracking.
 *
 * Features:
 * - Fetches available serial numbers (OnHand status) for the product
 * - Validates no duplicate serial numbers across work order items
 * - Displays selected serial numbers using Tags component
 */

export function InventoryBySerialNumberSelectInlineInput({
  editContent,
  initialValues,
  emptyContent,
  displayContent,
  parentInputComponent,
  validate,
  ...props
}: InlineInputProps<{ filter?: FilterQuery<any> }>) {
  const ParentInput = parentInputComponent || InlineInput

  return (
    <ParentInput
      initialValues={initialValues}
      emptyContent={emptyContent ?? InventoryBySerialNumberSelectInlineInput.defaultEmptyContent}
      displayContent={displayContent ?? InventoryBySerialNumberSelectInlineInput.defaultDisplayContent}
      editContent={editContent ?? InventoryBySerialNumberSelectInlineInput.defaultEditContent}
      validate={validate ?? InventoryBySerialNumberSelectInlineInput.validate(initialValues?.quantity ?? 0)}
      {...props}
    />
  )
}

InventoryBySerialNumberSelectInlineInput.defaultEmptyContent = () => {
  return '[Select Serial Numbers]'
}

InventoryBySerialNumberSelectInlineInput.defaultDisplayContent = (
  value: InventoryBySerialNumber[] | undefined,
  _onClick?: () => void,
  _options?: InlineInputContentOptions
) => {
  return <Tags dataArray={value} tagComponent={({ value: tagValue }) => <Badge>{tagValue.code}</Badge>} />
}

InventoryBySerialNumberSelectInlineInput.defaultEditContent = ({
  value,
  setNewValue,
  inputProps,
  initialValues,
  ...props
}: InlineInputContentOptions) => {
  const serialNumbers: InventoryBySerialNumber[] = initialValues?.inventoryBySerialNumbers ?? []

  const filter = {
    status: InventoryBySerialNumberStatusEnum.OnHand,
    'product._id': initialValues?.product?._id ?? null,
    //we need to make sure the serial numbers are not already selected
    code: { $nin: serialNumbers.map((e) => e.code) },

    ...inputProps?.filter,
  }

  return (
    <InventoryBySerialNumberSelect filter={filter} isMulti {...props} onChange={setNewValue} value={value} autoFocus />
  )
}

InventoryBySerialNumberSelectInlineInput.validate = (quantity: number) =>
  ((value: InventoryBySerialNumber) => {
    const isBatchSerial = value?.product?.serialNumberType === SerialNumberTypeEnum.Batch
    const requiredSerialNumber = value?.product?.requiredSerialNumber
    if (isEmpty(value)) return
    if (!isArray(value)) {
      throw new Error('Invalid serial number selection')
    }

    //if the quantity is not equal to the count of serial numbers, throw error
    if (quantity !== value.length) {
      throw new Error('The count of serial numbers should be equal to quantity.')
    }

    if (requiredSerialNumber) {
      if (!isBatchSerial) {
        const unique = uniqBy(value, '_id')
        if (unique.length !== value.length) {
          throw new Error('Duplicate serial numbers selected')
        }
      }
    }
  }) as InlineInputValidateFunction
